import { useMutation, useQuery } from '@apollo/client'
import {
    Autocomplete,
    AutocompleteProps,
    Box,
    Button,
    Dialog,
    DialogActions,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    GetClientsWithPortalAccessQuery,
    GetRecruitersByNameQuery,
    RosterProfileQuery,
    RosterProfileQueryVariables,
    RosterStatus,
} from '__generated__/graphql'
import { useState } from 'react'
import { GetRecruitersByUserIds } from 'shared/components/RecruitersAutocomplete/RecruitersAutocomplete.gql'
import RecruitersAutocompleteMultiple from 'shared/components/RecruitersAutocomplete/RecruitersAutocompleteMultiple'
import { RecruitersAutocompleteProps } from 'shared/components/RecruitersAutocomplete/RecruitersAutocompleteProps'
import useToast from 'shared/components/Toast/useToast'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getFullName, getPxAsRem } from 'shared/utils'
import { GetRosterData } from '../RosterDrawer/AddOrEdit.gql'
import {
    GetClientsWithPortalAccess,
    GetUsersWithRosterAccess,
    ShareRoster,
} from './ShareRoster.gql'

interface ShareRosterModalProps {
    rosterId: string
    open: boolean
    onClose: () => void
    refetch?: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
}

export default function ShareRosterModal(props: ShareRosterModalProps) {
    const { rosterId, open, onClose, refetch } = props
    const [selectedRecruiters, setSelectedRecruiters] = useState<
        GetRecruitersByNameQuery['recruiters']['data'][number][]
    >([])
    const [selectedClients, setSelectedClients] = useState<
        GetClientsWithPortalAccessQuery['getClientsWithPortalAccessForCompany'][number][]
    >([])
    const toast = useToast()

    const { data: rosterData } = useQuery(GetRosterData, {
        variables: { rosterId },
    })

    const [shareRoster] = useMutation(ShareRoster, {
        variables: {
            rosterId,
            recruiterIds: selectedRecruiters.map(
                (recruiter) => recruiter?.id ?? ''
            ),
            clientIds: selectedClients.map((client) => client?.id ?? ''),
        },
        onCompleted: (data) => {
            if (data?.shareRoster?.success) {
                onClose()
                toast({
                    message: 'Search shared successfully',
                    title: 'Success',
                    severity: 'success',
                })
            } else {
                console.error(data?.shareRoster?.message)
                toast({
                    message: 'Failed to share search',
                    title: 'Error',
                    severity: 'error',
                })
            }

            if (refetch) {
                refetch()
            }
        },
    })

    const { data: usersWithRosterAccess } = useQuery(GetUsersWithRosterAccess, {
        variables: { rosterId },
        onCompleted: (data) => {
            setSelectedClients(
                data?.roster?.sharedClients?.map(
                    (client, i) =>
                        client?.client ?? {
                            id: `unknown-client-${i}`, // Fallback in case client is null
                            user: {
                                id: `unknown-user-${i}`,
                                firstName: 'unknown',
                                lastName: 'unknown',
                                email: 'unknown',
                            },
                        }
                ) ?? []
            )
        },
    })

    useQuery(GetRecruitersByUserIds, {
        variables: {
            ids:
                usersWithRosterAccess?.roster?.sharedRecruiters?.map(
                    (recruiter) => recruiter?.recruiter?.user.id ?? ''
                ) ?? [],
        },
        onCompleted: (data) => {
            setSelectedRecruiters(data?.recruitersByUserId)
        },
    })

    const { data: clientsWithPortalAccess } = useQuery(
        GetClientsWithPortalAccess,
        {
            variables: { companyId: rosterData?.roster?.company?.id ?? '' },
            skip: !rosterData?.roster?.company?.id,
        }
    )

    const handleClientChange: AutocompleteProps<
        GetClientsWithPortalAccessQuery['getClientsWithPortalAccessForCompany'][number],
        true,
        false,
        false
    >['onChange'] = (e, newValues) => {
        setSelectedClients(newValues)
    }

    const handleRecruiterChange: RecruitersAutocompleteProps<true>['onChange'] =
        (e, newValues) => {
            setSelectedRecruiters(newValues)
        }

    const handleShareRoster = () => {
        shareRoster()
    }

    const isDisabled =
        rosterData?.roster?.status !== RosterStatus.Active ||
        !rosterData?.roster?.company?.id

    return (
        <Dialog open={open} onClose={onClose} title="Share Search">
            <Stack
                direction="column"
                spacing={1}
                alignItems={'flex-start'}
                justifyContent={'space-evenly'}
                display={'flex'}
                sx={{
                    padding: getPxAsRem(16, 24),
                    minWidth: getPxAsRem(600),
                    minHeight: getPxAsRem(425),
                }}
            >
                <Typography variant="h4">Share Roster</Typography>

                <Typography variant="body1">
                    Select internal users and clients with portal access to
                    share this roster with.
                </Typography>

                <RecruitersAutocompleteMultiple
                    label="Select Internal Users"
                    placeholder="Recruiters"
                    onChange={handleRecruiterChange}
                    value={selectedRecruiters}
                />

                <Autocomplete
                    fullWidth
                    multiple
                    options={
                        clientsWithPortalAccess?.getClientsWithPortalAccessForCompany ??
                        []
                    }
                    getOptionLabel={(option) =>
                        option?.user ? getFullName(option.user) : ''
                    }
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    onChange={handleClientChange}
                    value={selectedClients ?? []}
                    disabled={isDisabled}
                    noOptionsText="No clients with portal access found"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Client with Access"
                            placeholder="Client Portal User"
                        />
                    )}
                />

                {isDisabled && (
                    <Typography variant="body2">
                        In order to share this roster with clients, the roster
                        must be active and have a company associated with it.
                    </Typography>
                )}

                <Box width={'100%'} justifyContent="end" alignItems="end">
                    <DialogActions sx={{ padding: getPxAsRem(8) }}>
                        <Button variant="text" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleShareRoster}>Save</Button>
                    </DialogActions>
                </Box>
            </Stack>
        </Dialog>
    )
}
